/**
 * Owner-side helper to produce an updated ProfileBlob with new
 * about / emojiStatus / expiry fields.
 *
 * The builder validates inputs through ProfileStatusValidation so we never
 * create a blob containing about-text that exceeds the 130-scalar cap, or an
 * emoji status that fails the conservative single-scalar policy.
 *
 * The builder preserves `name`, `avatarHash`, and `schema` from the input
 * blob so the caller does not have to re-thread those fields and cannot
 * accidentally zero them.
 *
 * Threat model: the local user is trusted to set their own status, but we
 * still enforce validation here so a misbehaving local writer (e.g. a
 * clipboard paste that includes a ZWJ sequence) cannot ship a blob the
 * renderer would later reject.
 *
 * Mirrors: kaether_for_ios/Stoat/E2EE/ProfileStatusBuilder.swift
 */

import type { ProfileBlob } from "./ProfileBlob";
import { PROFILE_BLOB_CURRENT_SCHEMA } from "./ProfileBlob";
import { validateAbout, validateEmojiStatus } from "./ProfileStatusValidation";
import type {
  ProfileAboutValidationError,
  ProfileEmojiValidationError,
} from "./ProfileStatusValidation";

// ---------------------------------------------------------------------------
// Update parameter
// ---------------------------------------------------------------------------

export interface ProfileStatusUpdate {
  /** New about text, or undefined to clear. */
  readonly about?: string | null;
  /** New emoji status, or undefined to clear. */
  readonly emojiStatus?: string | null;
  /** New expiry (unix seconds), or undefined/null for no expiry. */
  readonly statusExpiresAt?: number | null;
}

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export type ProfileStatusBuilderError =
  | { readonly kind: "invalidAbout"; readonly error: ProfileAboutValidationError }
  | { readonly kind: "invalidEmoji"; readonly error: ProfileEmojiValidationError };

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

/**
 * Applies validation and returns an updated ProfileBlob.
 *
 * - `update.about == null` clears `about`.
 * - A non-null `update.about` that normalises to empty (whitespace-only)
 *   also clears `about` — "type spaces and submit" converges with "clear
 *   my about" (iOS parity).
 * - `update.emojiStatus == null` clears `emojiStatus`.
 * - A non-null `update.emojiStatus` is validated; any validator failure is
 *   returned as an error so the UI can surface it.
 * - `update.statusExpiresAt == null` means "no expiry"; stored verbatim.
 * - `name`, `avatarHash`, and `schema` are preserved from the input blob.
 *
 * Returns `{ ok: true, blob }` on success,
 * or `{ ok: false, error }` if emoji validation fails.
 *
 * Mirrors: ProfileStatusBuilder.buildBlobUpdates(currentBlob:newAbout:newEmojiStatus:statusExpiresAt:)
 */
export function updateStatus(
  currentBlob: ProfileBlob,
  update: ProfileStatusUpdate,
): { ok: true; blob: ProfileBlob } | { ok: false; error: ProfileStatusBuilderError } {
  // --- about ---
  let about: string | undefined;
  if (update.about != null) {
    const result = validateAbout(update.about);
    if (result.ok) {
      about = result.value;
    } else if (result.error.kind === "emptyAfterTrim") {
      // whitespace-only about treated as "clear"
      about = undefined;
    } else {
      // tooLong — propagate as a builder error so the UI can surface it.
      // (iOS chooses to surface tooLong rather than silently truncating.)
      return { ok: false, error: { kind: "invalidAbout", error: result.error } };
    }
  } else {
    // null or missing → clear
    about = undefined;
  }

  // --- emojiStatus ---
  let emojiStatus: string | undefined;
  if (update.emojiStatus != null) {
    const result = validateEmojiStatus(update.emojiStatus);
    if (!result.ok) {
      return { ok: false, error: { kind: "invalidEmoji", error: result.error } };
    }
    emojiStatus = result.value;
  } else {
    emojiStatus = undefined;
  }

  // --- expiry ---
  const statusExpiresAt =
    update.statusExpiresAt != null ? update.statusExpiresAt : null;

  const blob: ProfileBlob = {
    name: currentBlob.name,
    avatarHash: currentBlob.avatarHash,
    about,
    emojiStatus,
    statusExpiresAt,
    schema: currentBlob.schema ?? PROFILE_BLOB_CURRENT_SCHEMA,
  };

  return { ok: true, blob };
}
